import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import '../Games.css';
import {Player} from '../common/types';
import {loadJSON, saveJSON} from '../common/storage';
import PlayerSetup from '../common/PlayerSetup';
import CategoryChips from '../common/CategoryChips';
import {useAiContent} from '../common/useAiContent';
import DrawPromptCard from '../common/DrawPromptCard';
import {CUSTOM_CATEGORY, TelephoneItem} from './telephoneWords';

const MIN_PLAYERS = 3;
const PLAYERS_KEY = 'games_players';
const CUSTOM_WORDS_KEY = 'telephone_custom_words';
const DISABLED_CATEGORIES_KEY = 'telephone_disabled_categories';

type Stage = 'setup' | 'ready' | 'contributing' | 'reveal';
type StepType = 'draw' | 'guess';

interface AiWord {
    name: string;
    category: string;
}

interface ChainStep {
    playerId: string;
    playerName: string;
    type: StepType;
    value: string;
}

const GuessStep: React.FC<{image: string; onSubmit: (value: string) => void}> = ({image, onSubmit}) => {
    const [text, setText] = useState('');
    const submit = () => {
        const trimmed = text.trim();
        if (trimmed) onSubmit(trimmed);
    };
    return (
        <div className="GmCard">
            <p className="GmHint GmHint--warn">⚠ Не показывайте экран остальным!</p>
            <h3 className="GmCard-title">Что здесь нарисовано?</h3>
            <img className="GmTelephoneChain-image" src={image} alt="" style={{marginBottom: 12}}/>
            <input
                className="GmInput"
                placeholder="Твоя догадка"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') submit();
                }}
            />
            <div style={{marginTop: 16}}>
                <button className="GmBtn GmBtn--primary GmBtn--big" disabled={!text.trim()} onClick={submit}>
                    Готово, передать дальше
                </button>
            </div>
        </div>
    );
};

const TelephoneGame: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>(() => loadJSON(PLAYERS_KEY, []));
    const [customWords, setCustomWords] = useState<TelephoneItem[]>(() => loadJSON(CUSTOM_WORDS_KEY, []));
    const [disabledCategories, setDisabledCategories] = useState<string[]>(() => loadJSON(DISABLED_CATEGORIES_KEY, []));
    const [newWordText, setNewWordText] = useState('');

    const [stage, setStage] = useState<Stage>('setup');
    const [chainWord, setChainWord] = useState('');
    const [usedWords, setUsedWords] = useState<Set<string>>(new Set());
    const [steps, setSteps] = useState<ChainStep[]>([]);
    const [playerIndex, setPlayerIndex] = useState(0);

    const aiWords = useAiContent<AiWord>('telephone-words.json');

    const categories = useMemo(() => {
        const set = new Set(aiWords.map(w => w.category));
        return Array.from(set);
    }, [aiWords]);

    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        aiWords.forEach(w => {
            counts[w.category] = (counts[w.category] || 0) + 1;
        });
        return counts;
    }, [aiWords]);

    const allWords = useMemo((): TelephoneItem[] => [
        ...aiWords.map((w): TelephoneItem => ({name: w.name, category: w.category, ai: true})),
        ...customWords.map((w): TelephoneItem => ({...w, category: CUSTOM_CATEGORY})),
    ], [aiWords, customWords]);

    const activeWords = useMemo(
        () => allWords.filter(w => w.custom || !disabledCategories.includes(w.category || '')),
        [allWords, disabledCategories]
    );

    const updatePlayers = (next: Player[]) => {
        setPlayers(next);
        saveJSON(PLAYERS_KEY, next);
    };

    const toggleCategory = (category: string) => {
        const next = disabledCategories.includes(category)
            ? disabledCategories.filter(c => c !== category)
            : [...disabledCategories, category];
        setDisabledCategories(next);
        saveJSON(DISABLED_CATEGORIES_KEY, next);
    };

    const addCustomWord = () => {
        const name = newWordText.trim();
        if (!name) return;
        const next = [...customWords, {name, category: CUSTOM_CATEGORY, custom: true}];
        setCustomWords(next);
        saveJSON(CUSTOM_WORDS_KEY, next);
        setNewWordText('');
    };

    const removeCustomWord = (name: string) => {
        const next = customWords.filter(w => w.name !== name);
        setCustomWords(next);
        saveJSON(CUSTOM_WORDS_KEY, next);
    };

    const pickWord = (pool: TelephoneItem[], used: Set<string>): {item: TelephoneItem; nextUsed: Set<string>} => {
        let available = pool.filter(w => !used.has(w.name));
        let nextUsed = used;
        if (available.length === 0) {
            available = pool;
            nextUsed = new Set();
        }
        const item = available[Math.floor(Math.random() * available.length)];
        const updatedUsed = new Set(nextUsed);
        updatedUsed.add(item.name);
        return {item, nextUsed: updatedUsed};
    };

    const beginChain = (pool: TelephoneItem[], used: Set<string>) => {
        const {item, nextUsed} = pickWord(pool, used);
        setChainWord(item.name);
        setUsedWords(nextUsed);
        setSteps([]);
        setPlayerIndex(0);
        setStage('ready');
    };

    const startGame = () => {
        if (players.length < MIN_PLAYERS || activeWords.length === 0) return;
        beginChain(activeWords, new Set());
    };

    const newChain = () => beginChain(activeWords, usedWords);

    const backToSetup = () => {
        setStage('setup');
        setSteps([]);
    };

    const currentPlayer = players[playerIndex];
    const stepType: StepType = playerIndex % 2 === 0 ? 'draw' : 'guess';
    const promptValue = playerIndex === 0 ? chainWord : steps[playerIndex - 1].value;

    const handleStepSubmit = (value: string) => {
        if (!currentPlayer) return;
        setSteps(s => [...s, {playerId: currentPlayer.id, playerName: currentPlayer.name, type: stepType, value}]);
        const next = playerIndex + 1;
        if (next >= players.length) {
            setStage('reveal');
        } else {
            setPlayerIndex(next);
            setStage('ready');
        }
    };

    return (
        <div className={`GmPage ${stage === 'setup' ? '' : 'GmPage--noBar'}`}>
            <div className="GmContainer">
                <div className="GmTopBar">
                    <Link to="/games" className="GmBack">← Игры</Link>
                    <h1 className="GmTitle" style={{margin: 0}}>Испорченный телефон</h1>
                </div>

                {stage === 'setup' && (
                    <>
                        <p className="GmSubtitle">
                            Первый рисует загаданное слово. Следующий видит только рисунок (без слова) и пишет,
                            что на нём изображено. Следующий рисует уже эту догадку — и так по кругу. В финале
                            смотрите, во что превратилось исходное слово.
                        </p>

                        <PlayerSetup players={players} setPlayers={updatePlayers} minPlayers={MIN_PLAYERS}/>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Категории ({activeWords.length} слов активно)</h3>

                            {categories.length === 0 && customWords.length === 0 && (
                                <p className="GmHint GmHint--warn">
                                    Пока нет ни одного слова. Сгенерируйте пул скриптом
                                    scripts/generate-ai-content.js telephone all 40 или добавьте свои слова ниже.
                                </p>
                            )}

                            {categories.length > 0 && (
                                <CategoryChips
                                    categories={categories}
                                    counts={categoryCounts}
                                    disabled={disabledCategories}
                                    onToggle={toggleCategory}
                                />
                            )}

                            {customWords.length > 0 && (
                                <div className="GmList" style={{marginTop: 12}}>
                                    {customWords.map(w => (
                                        <div className="GmListItem" key={w.name}>
                                            <span className="GmListItem-name">{w.name}</span>
                                            <span className="GmCheckItem-custom">своё</span>
                                            <button className="GmIconBtn" onClick={() => removeCustomWord(w.name)} aria-label="Удалить">✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="GmInputRow" style={{marginTop: 12}}>
                                <input
                                    className="GmInput"
                                    placeholder="Своё слово для рисования"
                                    value={newWordText}
                                    onChange={(e) => setNewWordText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') addCustomWord();
                                    }}
                                />
                                <button className="GmBtn" onClick={addCustomWord}>Добавить</button>
                            </div>
                        </div>

                        <div className="GmBottomBar">
                            <div className="GmBottomBar-inner">
                                <button
                                    className="GmBtn GmBtn--primary GmBtn--big"
                                    disabled={players.length < MIN_PLAYERS || activeWords.length === 0}
                                    onClick={startGame}
                                >
                                    Начать цепочку
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'ready' && currentPlayer && (
                    <div className="GmPassCard">
                        <p className="GmPassCard-hint">Передайте телефон игроку</p>
                        <h2 className="GmPassCard-name">{currentPlayer.name}</h2>
                        <p className="GmHint">
                            {stepType === 'draw' ? 'Сейчас нужно будет нарисовать слово' : 'Сейчас нужно будет угадать рисунок'}
                        </p>
                        <button
                            className="GmBtn GmBtn--primary GmBtn--big"
                            onClick={() => setStage('contributing')}
                        >
                            Я — {currentPlayer.name}, начать
                        </button>
                        <p className="GmPassCard-progress">{playerIndex + 1} / {players.length}</p>
                    </div>
                )}

                {stage === 'contributing' && currentPlayer && stepType === 'draw' && (
                    <DrawPromptCard
                        label="Нарисуй:"
                        prompt={promptValue}
                        warning="⚠ Не показывайте экран остальным!"
                        onSubmit={handleStepSubmit}
                    />
                )}

                {stage === 'contributing' && currentPlayer && stepType === 'guess' && (
                    <GuessStep image={promptValue} onSubmit={handleStepSubmit}/>
                )}

                {stage === 'reveal' && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Что получилось</h3>
                        <p className="GmHint">Загаданное слово</p>
                        <p className="GmRoleLocation">{chainWord}</p>
                        <div className="GmTelephoneChain">
                            {steps.map((step, i) => (
                                <div className="GmTelephoneChain-step" key={i}>
                                    <p className="GmHint">{step.playerName} {step.type === 'draw' ? 'нарисовал(а)' : 'написал(а)'}</p>
                                    {step.type === 'draw'
                                        ? <img className="GmTelephoneChain-image" src={step.value} alt=""/>
                                        : <p className="GmSituationText">{step.value}</p>}
                                </div>
                            ))}
                        </div>
                        <div className="GmBtnRow" style={{marginTop: 16}}>
                            <button className="GmBtn GmBtn--primary" onClick={newChain}>Новая цепочка</button>
                            <button className="GmBtn" onClick={backToSetup}>Изменить игроков</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TelephoneGame;
